import type { CreateJobInput, JobSource } from '@chukua/common';
import { Alert, Button, Form, Select } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import type { JobType, Zone } from '../reference-data/reference-data.api';
import { CustomerSearchSelect } from './CustomerSearchSelect';
import { JobIntakeDetailFields } from './JobIntakeDetailFields';
import type { JobDetail } from './jobs.api';

interface JobIntakeFormValues {
  customerId: string;
  jobTypeCode: string;
  source?: JobSource;
  contactName?: string;
  contactPhone?: string;
  requestedQuantity?: number;
  originZoneCode?: string;
  destinationZoneCode?: string;
  requestedDate?: Dayjs;
  customerDeliveryDeadline?: Dayjs;
}

interface JobIntakeFormProps {
  job: JobDetail | null;
  jobTypes: JobType[];
  zones: Zone[];
  error: string | null;
  saving: boolean;
  onSave: (input: CreateJobInput) => Promise<void>;
}

function toFormValues(job: JobDetail | null): Partial<JobIntakeFormValues> {
  if (!job) return {};
  return {
    customerId: job.customerId,
    jobTypeCode: job.jobTypeCode,
    source: (job.source as JobSource | null) ?? undefined,
    contactName: job.contactName ?? undefined,
    contactPhone: job.contactPhone ?? undefined,
    requestedQuantity: job.requestedQuantity ?? undefined,
    originZoneCode: job.originZoneCode ?? undefined,
    destinationZoneCode: job.destinationZoneCode ?? undefined,
    requestedDate: job.requestedDate ? dayjs(job.requestedDate) : undefined,
    customerDeliveryDeadline: job.customerDeliveryDeadline ? dayjs(job.customerDeliveryDeadline) : undefined,
  };
}

function toApiInput(values: JobIntakeFormValues): CreateJobInput {
  return {
    customerId: values.customerId,
    jobTypeCode: values.jobTypeCode,
    source: values.source,
    contactName: values.contactName || undefined,
    contactPhone: values.contactPhone || undefined,
    requestedQuantity: values.requestedQuantity,
    originZoneCode: values.originZoneCode,
    destinationZoneCode: values.destinationZoneCode,
    requestedDate: values.requestedDate?.format('YYYY-MM-DD'),
    customerDeliveryDeadline: values.customerDeliveryDeadline?.format('YYYY-MM-DD'),
  };
}

/**
 * Quick-create when `job` is null — only customer and job type required, everything else
 * fillable now or later (PRD → Job intake; gitaumoses4/chukua#30). Once a job exists,
 * customer and job type are immutable (out of scope) and disabled here, not hidden — the
 * clerk should see what was captured, not lose the field from view.
 */
export function JobIntakeForm({ job, jobTypes, zones, error, saving, onSave }: JobIntakeFormProps) {
  const [form] = Form.useForm<JobIntakeFormValues>();
  const isNew = job === null;

  useEffect(() => {
    form.setFieldsValue(toFormValues(job));
  }, [job, form]);

  async function submit(values: JobIntakeFormValues) {
    await onSave(toApiInput(values));
  }

  return (
    <Form<JobIntakeFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={saving} initialValues={toFormValues(job)}>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <div className="grid grid-cols-2 gap-3">
        <Form.Item name="customerId" label="Customer" rules={[{ required: true, message: 'Select a customer' }]}>
          <CustomerSearchSelect disabled={!isNew} />
        </Form.Item>
        <Form.Item name="jobTypeCode" label="Job type" rules={[{ required: true, message: 'Select a job type' }]}>
          <Select disabled={!isNew} options={jobTypes.map((t) => ({ value: t.code, label: t.name }))} showSearch optionFilterProp="label" />
        </Form.Item>
      </div>

      <JobIntakeDetailFields zones={zones} />

      <Form.Item className="mb-0">
        <Button type="primary" htmlType="submit" loading={saving}>
          {isNew ? 'Create job' : 'Save changes'}
        </Button>
      </Form.Item>
    </Form>
  );
}
